import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Pause,
  Square,
  Plus,
  Trash2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Building2,
  Layers,
  Factory,
  Database,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import {
  useResearchQueueStore,
  QueueTask,
  QueueTaskType,
  QueueTaskStatus,
  QueueStatus,
  getTaskTypeLabel,
  getStatusColor
} from '../stores/researchQueueStore';

// ============================================================================
// Icon Helper
// ============================================================================

const TaskIcon: React.FC<{ taskType: QueueTaskType; className?: string }> = ({ taskType, className = 'w-5 h-5' }) => {
  switch (taskType) {
    case 'company_research':
      return <Building2 className={className} />;
    case 'domain_research':
      return <Layers className={className} />;
    case 'industry_research':
      return <Factory className={className} />;
    case 'system_research':
      return <Database className={className} />;
    case 'financial_reports':
      return <FileText className={className} />;
    default:
      return <AlertCircle className={className} />;
  }
};

const StatusIcon: React.FC<{ status: QueueTaskStatus; className?: string }> = ({ status, className = 'w-4 h-4' }) => {
  switch (status) {
    case 'running':
      return <Loader2 className={`${className} animate-spin text-blue-500`} />;
    case 'completed':
      return <CheckCircle2 className={`${className} text-green-500`} />;
    case 'failed':
      return <XCircle className={`${className} text-red-500`} />;
    case 'queued':
      return <Clock className={`${className} text-gray-400`} />;
    default:
      return <AlertCircle className={`${className} text-gray-400`} />;
  }
};

// ============================================================================
// Add Task Modal
// ============================================================================

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose }) => {
  const [taskType, setTaskType] = useState<QueueTaskType>('company_research');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    addCompanyTask,
    addDomainTask,
    addIndustryTask,
    addSystemTask
  } = useResearchQueueStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      switch (taskType) {
        case 'company_research':
          await addCompanyTask({
            companyName: formData.companyName || '',
            cik: formData.cik,
            ticker: formData.ticker,
            timeRange: formData.timeRange || '5years'
          });
          break;
        case 'domain_research':
          await addDomainTask({
            domainName: formData.domainName || '',
            description: formData.description,
            industry: formData.industry
          });
          break;
        case 'industry_research':
          await addIndustryTask({
            industryName: formData.industryName || '',
            subcategory: formData.subcategory
          });
          break;
        case 'system_research':
          await addSystemTask({
            systemName: formData.systemName || '',
            vendor: formData.vendor,
            category: formData.category
          });
          break;
      }

      toast.success('Task added to queue');
      setFormData({});
      onClose();
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Add Research Task</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Task Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { type: 'company_research' as QueueTaskType, label: 'Company', icon: Building2 },
                { type: 'domain_research' as QueueTaskType, label: 'Domain', icon: Layers },
                { type: 'industry_research' as QueueTaskType, label: 'Industry', icon: Factory },
                { type: 'system_research' as QueueTaskType, label: 'System', icon: Database },
              ].map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setTaskType(type);
                    setFormData({});
                  }}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    taskType === type
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Form Fields */}
          <div className="space-y-3">
            {taskType === 'company_research' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.companyName || ''}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., Apple Inc."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CIK Number
                    </label>
                    <input
                      type="text"
                      value={formData.cik || ''}
                      onChange={(e) => setFormData({ ...formData, cik: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="e.g., 0000320193"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ticker
                    </label>
                    <input
                      type="text"
                      value={formData.ticker || ''}
                      onChange={(e) => setFormData({ ...formData, ticker: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="e.g., AAPL"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Range
                  </label>
                  <select
                    value={formData.timeRange || '5years'}
                    onChange={(e) => setFormData({ ...formData, timeRange: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  >
                    <option value="1year">1 Year</option>
                    <option value="2years">2 Years</option>
                    <option value="3years">3 Years</option>
                    <option value="5years">5 Years</option>
                    <option value="10years">10 Years</option>
                  </select>
                </div>
              </>
            )}

            {taskType === 'domain_research' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Domain Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.domainName || ''}
                    onChange={(e) => setFormData({ ...formData, domainName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., Supply Chain Management"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <input
                    type="text"
                    value={formData.industry || ''}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., Manufacturing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Brief description of the domain..."
                    rows={2}
                  />
                </div>
              </>
            )}

            {taskType === 'industry_research' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.industryName || ''}
                    onChange={(e) => setFormData({ ...formData, industryName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., Semiconductor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subcategory
                  </label>
                  <input
                    type="text"
                    value={formData.subcategory || ''}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., Memory Chips"
                  />
                </div>
              </>
            )}

            {taskType === 'system_research' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    System Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.systemName || ''}
                    onChange={(e) => setFormData({ ...formData, systemName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="e.g., SAP S/4HANA"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor
                    </label>
                    <input
                      type="text"
                      value={formData.vendor || ''}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="e.g., SAP"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category || ''}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="">Select...</option>
                      <option value="ERP">ERP</option>
                      <option value="CRM">CRM</option>
                      <option value="SCM">SCM</option>
                      <option value="PLM">PLM</option>
                      <option value="MES">MES</option>
                      <option value="WMS">WMS</option>
                      <option value="BI">BI/Analytics</option>
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-amber-500 hover:bg-amber-600"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Queue
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Task Card Component
// ============================================================================

interface TaskCardProps {
  task: QueueTask;
  onRemove?: (taskId: string) => void;
  onMoveUp?: (taskId: string) => void;
  onMoveDown?: (taskId: string) => void;
  onRetry?: (taskId: string) => void;
  showControls?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onRemove,
  onMoveUp,
  onMoveDown,
  onRetry,
  showControls = true,
  isFirst = false,
  isLast = false
}) => {
  return (
    <div className={`p-4 rounded-lg border ${
      task.status === 'running' ? 'border-blue-300 bg-blue-50' :
      task.status === 'failed' ? 'border-red-200 bg-red-50' :
      task.status === 'completed' ? 'border-green-200 bg-green-50' :
      'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-lg ${
          task.status === 'running' ? 'bg-blue-100' :
          task.status === 'failed' ? 'bg-red-100' :
          task.status === 'completed' ? 'bg-green-100' :
          'bg-gray-100'
        }`}>
          <TaskIcon taskType={task.taskType} className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 truncate">{task.name}</h4>
            <Badge className={getStatusColor(task.status)}>
              {task.status}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {getTaskTypeLabel(task.taskType)}
          </p>

          {/* Progress Bar for Running Tasks */}
          {task.status === 'running' && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{task.progressMessage || 'Processing...'}</span>
                <span>{task.progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${task.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error Message */}
          {task.status === 'failed' && task.error && (
            <p className="text-sm text-red-600 mt-2">{task.error}</p>
          )}
        </div>

        {/* Controls */}
        {showControls && (
          <div className="flex items-center gap-1">
            {task.status === 'queued' && (
              <>
                {!isFirst && onMoveUp && (
                  <button
                    onClick={() => onMoveUp(task.taskId)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                )}
                {!isLast && onMoveDown && (
                  <button
                    onClick={() => onMoveDown(task.taskId)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
                {onRemove && (
                  <button
                    onClick={() => onRemove(task.taskId)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
            {task.status === 'failed' && onRetry && task.retryCount < task.maxRetries && (
              <button
                onClick={() => onRetry(task.taskId)}
                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                title="Retry"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

export default function ResearchQueue() {
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    queueStatus,
    currentTask,
    queuedTasks,
    completedTasks,
    failedTasks,
    statistics,
    isLoading,
    error,
    fetchQueueStatus,
    removeTask,
    reorderTask,
    retryTask,
    startQueue,
    pauseQueue,
    resumeQueue,
    stopQueue,
    clearCompleted,
    startPolling,
    stopPolling
  } = useResearchQueueStore();

  // Fetch initial data and set up polling
  useEffect(() => {
    fetchQueueStatus();

    // Start polling if queue is running
    if (queueStatus === 'running') {
      startPolling(5000);
    }

    return () => {
      stopPolling();
    };
  }, []);

  // Update polling based on queue status
  useEffect(() => {
    if (queueStatus === 'running') {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [queueStatus]);

  const handleMoveUp = async (taskId: string) => {
    const task = queuedTasks.find(t => t.taskId === taskId);
    if (task && task.position > 0) {
      await reorderTask(taskId, task.position - 1);
    }
  };

  const handleMoveDown = async (taskId: string) => {
    const task = queuedTasks.find(t => t.taskId === taskId);
    if (task && task.position < queuedTasks.length - 1) {
      await reorderTask(taskId, task.position + 1);
    }
  };

  const handleRemove = async (taskId: string) => {
    if (window.confirm('Are you sure you want to remove this task?')) {
      await removeTask(taskId);
      toast.success('Task removed');
    }
  };

  const handleRetry = async (taskId: string) => {
    await retryTask(taskId);
    toast.success('Task queued for retry');
  };

  const handleStartQueue = async () => {
    await startQueue();
    toast.success('Queue started');
  };

  const handlePauseQueue = async () => {
    await pauseQueue();
    toast.info('Queue paused');
  };

  const handleResumeQueue = async () => {
    await resumeQueue();
    toast.success('Queue resumed');
  };

  const handleStopQueue = async () => {
    await stopQueue();
    toast.info('Queue stopped');
  };

  const handleClearCompleted = async () => {
    await clearCompleted();
    toast.success('Completed tasks cleared');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-8">
      <div className="max-w-screen-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
                <Database className="w-8 h-8 text-amber-600" />
                Research Queue
              </h1>
              <p className="text-neutral-600 mt-1">
                Batch process research tasks sequentially
              </p>
            </div>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Research
            </Button>
          </div>
        </div>

        {/* Queue Status Bar */}
        <Card className="mb-6 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                queueStatus === 'running' ? 'bg-blue-100 text-blue-700' :
                queueStatus === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                queueStatus === 'stopped' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {queueStatus === 'running' && <Loader2 className="w-4 h-4 inline mr-1.5 animate-spin" />}
                {queueStatus.charAt(0).toUpperCase() + queueStatus.slice(1)}
              </div>

              {statistics && (
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{queuedTasks.length} queued</span>
                  <span className="text-green-600">{statistics.completedTasks} completed</span>
                  {statistics.failedTasks > 0 && (
                    <span className="text-red-600">{statistics.failedTasks} failed</span>
                  )}
                  {queuedTasks.length > 0 && (
                    <span className="text-gray-400">Est. {statistics.estimatedTimeRemaining}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {queueStatus === 'idle' && queuedTasks.length > 0 && (
                <Button onClick={handleStartQueue} size="sm" className="bg-green-500 hover:bg-green-600">
                  <Play className="w-4 h-4 mr-1.5" />
                  Start
                </Button>
              )}
              {queueStatus === 'running' && (
                <Button onClick={handlePauseQueue} size="sm" variant="outline">
                  <Pause className="w-4 h-4 mr-1.5" />
                  Pause
                </Button>
              )}
              {queueStatus === 'paused' && (
                <>
                  <Button onClick={handleResumeQueue} size="sm" className="bg-green-500 hover:bg-green-600">
                    <Play className="w-4 h-4 mr-1.5" />
                    Resume
                  </Button>
                  <Button onClick={handleStopQueue} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                    <Square className="w-4 h-4 mr-1.5" />
                    Stop
                  </Button>
                </>
              )}
              <Button onClick={() => fetchQueueStatus()} size="sm" variant="ghost">
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Task */}
          {currentTask && (
            <div className="lg:col-span-3">
              <Card className="p-4 border-2 border-blue-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Currently Processing
                </h3>
                <TaskCard task={currentTask} showControls={false} />
              </Card>
            </div>
          )}

          {/* Queued Tasks */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  Queue ({queuedTasks.length})
                </h3>
              </CardHeader>
              <CardBody className="p-4">
                {queuedTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No tasks in queue</p>
                    <Button
                      onClick={() => setIsAddModalOpen(true)}
                      variant="outline"
                      className="mt-4"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Research Task
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {queuedTasks.map((task, index) => (
                      <TaskCard
                        key={task.taskId}
                        task={task}
                        onRemove={handleRemove}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        isFirst={index === 0}
                        isLast={index === queuedTasks.length - 1}
                      />
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Sidebar: Completed & Failed */}
          <div className="space-y-6">
            {/* Completed Tasks */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Completed ({completedTasks.length})
                </h3>
                {completedTasks.length > 0 && (
                  <button
                    onClick={handleClearCompleted}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear all
                  </button>
                )}
              </CardHeader>
              <CardBody className="p-4">
                {completedTasks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No completed tasks
                  </p>
                ) : (
                  <div className="space-y-2">
                    {completedTasks.slice(0, 5).map((task) => (
                      <div
                        key={task.taskId}
                        className="flex items-center gap-2 p-2 rounded bg-gray-50"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{task.name}</span>
                      </div>
                    ))}
                    {completedTasks.length > 5 && (
                      <p className="text-xs text-gray-400 text-center">
                        +{completedTasks.length - 5} more
                      </p>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Failed Tasks */}
            {failedTasks.length > 0 && (
              <Card className="border-red-200">
                <CardHeader>
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    Failed ({failedTasks.length})
                  </h3>
                </CardHeader>
                <CardBody className="p-4">
                  <div className="space-y-2">
                    {failedTasks.map((task) => (
                      <TaskCard
                        key={task.taskId}
                        task={task}
                        onRetry={handleRetry}
                        showControls={true}
                      />
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  );
}
